import { useContext } from "react";
import { useHistory } from "react-router";
import { buildLink, RouteNames } from "common/routing";
import {
  isSubmissionTable,
  SUBMISSION_TABLE_TO_CATEGORY,
  TABLE_NAMES,
  TopLevelTableNames,
} from "common/utils/queryBuilder";
import { generateDisplayName } from "common-client/utils/mapLayers";
import { colors } from "../../stitches.config";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexRow } from "../Common/__styles__/Layout";
import { StyledLink } from "../Common/__styles__/Typography";
import { BottomBanner } from "../Common/BottomBanner";
import { Button } from "../Common/Buttons/Button";
import { LayerContext, SavedView } from "./layers";
import { SAVED_VIEW_MIN_ZOOM } from "./layers/savedViews";

const TABLE_NAME_TO_ROUTE: Record<TopLevelTableNames, RouteNames> = {
  [TABLE_NAMES.PROPERTIES]: "properties",
  [TABLE_NAMES.FILES]: "documentUploads",
  [TABLE_NAMES.SUBMISSIONS]: "recordsTable",
  [TABLE_NAMES.PERMITS]: "recordsTable",
  [TABLE_NAMES.INSPECTIONS]: "recordsTable",
  [TABLE_NAMES.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE]: "recordsTable",
  [TABLE_NAMES.CODE_CASES]: "recordsTable",
  [TABLE_NAMES.GRANTS]: "recordsTable",
  [TABLE_NAMES.INSURANCE]: "recordsTable",
  [TABLE_NAMES.MAILINGS]: "recordsTable",
  [TABLE_NAMES.PLANNING]: "recordsTable",
  [TABLE_NAMES.PROJECTS]: "recordsTable",
  [TABLE_NAMES.PAYMENTS]: "recordsTable",
  [TABLE_NAMES.COMPLAINTS]: "recordsTable",
  [TABLE_NAMES.ASSESSMENTS]: "recordsTable",
  [TABLE_NAMES.ZONING_REVIEWS]: "recordsTable",
  [TABLE_NAMES.TICKETS]: "recordsTable",
  [TABLE_NAMES.TAXES]: "recordsTable",
  [TABLE_NAMES.RECORDS]: "recordsTable",
  [TABLE_NAMES.WORK_ORDERS]: "recordsTable",
  [TABLE_NAMES.FIRE_PREVENTION]: "recordsTable",
  [TABLE_NAMES.CUSTOM_MAP_GEOMETRIES]: "objectsTable",
  [TABLE_NAMES.TASKS]: "tasks",
  [TABLE_NAMES.WORKFLOWS]: "workflowsTable",
};

export const SavedViewBanner = ({
  visibleSavedView,
  zoom,
  infoPanelOpen,
}: {
  visibleSavedView?: Maybe<SavedView>;
  zoom: number;
  infoPanelOpen: boolean;
}) => {
  const { isPublic, account } = useContext(AuthContext);
  const { toggleLayer } = useContext(LayerContext);
  const history = useHistory();

  const customObjectGroups = account?.customObjectGroups ?? [];

  if (!visibleSavedView) {
    return null;
  }

  let category = null;
  let groupId = null;

  const table = visibleSavedView.query.source.table;
  if (isSubmissionTable(table)) {
    category = SUBMISSION_TABLE_TO_CATEGORY[table];
  } else if (table === TABLE_NAMES.CUSTOM_MAP_GEOMETRIES) {
    groupId = visibleSavedView.query.source.tableSubset?.value;
  }

  const link =
    isPublic ?
      buildLink("residentSavedView", { savedViewId: visibleSavedView.id })
    : buildLink(
        TABLE_NAME_TO_ROUTE[table as keyof typeof TABLE_NAME_TO_ROUTE],
        { ...(category ? { category } : {}), ...(groupId ? { groupId } : {}) },
        { view: visibleSavedView.id },
      );

  const onClose = () => {
    toggleLayer({
      group: "savedViews",
      id: visibleSavedView.id,
      isVisible: false,
    });
  };

  const subtitle =
    zoom < SAVED_VIEW_MIN_ZOOM ? "Zoom in to view data." : undefined;

  return (
    <BottomBanner
      title={generateDisplayName({
        name: visibleSavedView.name,
        query: visibleSavedView.query,
        customObjectGroups,
      })}
      subtitle={subtitle}
      onClose={onClose}
      style="table"
      infoPanelOpen={infoPanelOpen}
    >
      <FlexRow gap="m" style={{ alignItems: "center" }}>
        <Button
          variant="outlineInverted"
          size="small"
          iconStart="table-properties"
          onPress={() => {
            track("Clicked Back to table", {
              savedViewId: visibleSavedView.id,
            });
            history.push(link);
          }}
          title="Back to table"
        />

        <StyledLink
          style={{
            color: colors.contentInfoInverted.toString(),
            cursor: "pointer",
          }}
          onClick={onClose}
          as="div"
        >
          Close view
        </StyledLink>
      </FlexRow>
    </BottomBanner>
  );
};
